import { Controller } from "@hotwired/stimulus"
import { Modal, Tooltip } from "bootstrap"
import { launchConfetti } from "../utils/confetti"

export default class extends Controller {
  static targets = ["results", "form", "radiusDisplay", "panelBody", "specialtyButton", "scrollHint", "networkLoadMore"]

  // "Show more" for the Your Network list. Explicit (not scroll-triggered)
  // so the suggestions below stay reachable by scrolling.
  async loadMoreYourNetwork(event) {
    const button = event.currentTarget
    const nextPage = parseInt(button.dataset.page, 10) + 1
    const url = new URL(button.dataset.url, window.location.origin)
    url.searchParams.set('page', nextPage)
    url.searchParams.set('per_page', button.dataset.perPage)

    button.disabled = true
    try {
      const response = await fetch(url, { headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' } })
      const html = await response.text()
      const wrapper = document.createElement('div')
      wrapper.innerHTML = html
      const pageContent = wrapper.querySelector('[data-has-more]')
      if (!pageContent) return

      document.getElementById('your-network-list')?.insertAdjacentHTML('beforeend', pageContent.innerHTML)
      button.dataset.page = nextPage
      if (pageContent.dataset.hasMore !== 'true') button.closest('div').remove()
      this.initializeTooltips()
    } catch (error) {
      console.error('Load more network error:', error)
    } finally {
      button.disabled = false
    }
  }

  // Hide the bouncing scroll-down arrow once the suggestion cards come into
  // view on their own (fires whenever the header re-renders with the hint)
  scrollHintTargetConnected(hint) {
    if (!this.hasPanelBodyTarget) return

    const cards = hint.closest('#network-suggestions-header')?.nextElementSibling
    if (!cards) return

    this.scrollHintObserver?.disconnect()
    this.scrollHintObserver = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        hint.remove()
        this.scrollHintObserver?.disconnect()
      }
    }, { root: this.panelBodyTarget })
    this.scrollHintObserver.observe(cards)
  }

  scrollHintTargetDisconnected() {
    this.scrollHintObserver?.disconnect()
  }

  connect() {
    this.timeout = null
    this.initializeTooltips()

    // The organization modal's actions are links; without this a Back visit
    // would restore the snapshot with the modal and backdrop still open.
    this.closeOrganizationModalForCache = this.closeOrganizationModalForCache.bind(this)
    document.addEventListener("turbo:before-cache", this.closeOrganizationModalForCache)

    // Close filter dropdowns when clicking outside
    this.outsideClickHandler = (event) => {
      if (!event.target.closest(".user-home-network .filter-item")) {
        this.closeAllDropdowns()
      }
    }
    document.addEventListener("click", this.outsideClickHandler)

    // Reinitialize tooltips when infinite scroll loads more items
    this.element.addEventListener('infinite-scroll:loaded', () => {
      this.initializeTooltips()
    })

    // Track the pointer position of the most recent click so the confetti can
    // burst from exactly where the user clicked (capture phase, before Turbo)
    this.element.addEventListener('pointerdown', (event) => {
      this.lastPointerDown = { x: event.clientX, y: event.clientY, at: Date.now() }
    }, true)

    // On submit: capture the confetti origin and flip the button to its green
    // "Added" state for instant feedback. Exact-match the create endpoint so
    // the star toggle (/network_memberships/toggle_star) doesn't trigger this.
    this.element.addEventListener('turbo:submit-start', (event) => {
      if (!this.isAddToNetworkForm(event.target)) return

      const button = event.target.querySelector('button')
      if (button) {
        // Prefer the actual click point; fall back to the button center
        // (e.g. keyboard submission)
        if (this.lastPointerDown && Date.now() - this.lastPointerDown.at < 1000) {
          this.confettiOrigin = { x: this.lastPointerDown.x, y: this.lastPointerDown.y }
        } else {
          const rect = button.getBoundingClientRect()
          this.confettiOrigin = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
        }
        this.markButtonAdded(button)
      }
    })

    // After an "Add to network" turbo stream inserts the member card:
    // confetti, let the suggestion card linger for a moment, then collapse it
    this.element.addEventListener('turbo:submit-end', (event) => {
      if (this.isAddToNetworkForm(event.target)) {
        if (event.detail?.success) {
          if (this.confettiOrigin) {
            launchConfetti(this.confettiOrigin, { size: 420 })
            this.confettiOrigin = null
          }
          this.scheduleSuggestionCardRemoval(event.target)
        } else {
          this.restoreAddButton(event.target.querySelector('button'))
        }
      }
      setTimeout(() => this.initializeTooltips(), 100)
    })
  }

  // Scrolls the panel so the suggestions header pins to the top with the
  // suggestion cards visible beneath it
  scrollToSuggestions() {
    if (!this.hasPanelBodyTarget) return

    if (this.hasScrollHintTarget) this.scrollHintTarget.remove()

    const panel = this.panelBodyTarget
    const header = panel.querySelector('#network-suggestions-header')
    const cards = header?.nextElementSibling
    if (!header || !cards) return

    // The cards container's true position (the sticky header reports its
    // pinned rect, not its natural one)
    const top = cards.getBoundingClientRect().top - panel.getBoundingClientRect().top + panel.scrollTop - header.offsetHeight
    panel.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
  }

  isAddToNetworkForm(form) {
    try {
      return new URL(form.action, window.location.origin).pathname === '/network_memberships'
    } catch {
      return false
    }
  }

  // Figma 22610:40462 - green "Added" confirmation state
  markButtonAdded(button) {
    button.dataset.originalHtml = button.innerHTML
    // bg-white sets background !important, so it must go for the green to show
    button.classList.remove('text-blue', 'bg-white')
    button.disabled = true
    button.style.setProperty('background', '#E6FFEF', 'important')
    button.style.borderColor = '#408558'
    button.style.color = '#408558'
    button.innerHTML = '<span class="d-flex align-items-center justify-content-center flex-shrink-0" style="width: 24px; height: 24px;"><i class="fa-solid fa-check" style="font-size: 10px;"></i></span><span class="font-12 fw-600">Added</span>'
  }

  restoreAddButton(button) {
    if (!button || button.dataset.originalHtml === undefined) return

    button.innerHTML = button.dataset.originalHtml
    button.classList.add('text-blue', 'bg-white')
    button.disabled = false
    button.style.removeProperty('background')
    button.style.borderColor = ''
    button.style.color = ''
    delete button.dataset.originalHtml
  }

  scheduleSuggestionCardRemoval(form) {
    const card = form.closest('[id^="suggestion_org_"]')
    if (!card) return

    // Turbo re-enables the submit button after the request; keep it disabled
    // through the linger so the "Added" state can't be clicked again
    setTimeout(() => {
      const button = form.querySelector('button')
      if (button) button.disabled = true
    }, 0)

    setTimeout(() => {
      card.style.overflow = 'hidden'
      card.style.height = `${card.offsetHeight}px`
      card.style.transition = 'opacity 0.3s ease, height 0.3s ease, margin-bottom 0.3s ease'
      requestAnimationFrame(() => {
        card.style.opacity = '0'
        card.style.height = '0px'
        card.style.marginBottom = '0'
      })
      setTimeout(() => card.remove(), 350)
    }, 1000)
  }

  initializeTooltips() {
    const tooltipTriggerList = this.element.querySelectorAll('[data-bs-toggle="tooltip"]')
    tooltipTriggerList.forEach(el => new Tooltip(el))
  }

  toggleDropdown(event) {
    // Close all other dropdowns first
    this.element.querySelectorAll(".custom-dropdown").forEach((dropdown) => {
      if (!dropdown.closest(".filter-item").contains(event.target)) {
        dropdown.classList.add("d-none")
      }
    })

    const dropdown = event.target
      .closest(".filter-item")
      .querySelector(".custom-dropdown")

    dropdown.classList.toggle("d-none")
  }

  clickDropdownFilterItem(event) {
    const dropdownFilterItem = event.target.closest(".dropdown-filter-item")
    const dropdown = event.target.closest(".custom-dropdown")
    const el = dropdown.querySelector('input.filter-input[type="hidden"]')
    const selectedValue = dropdownFilterItem.dataset.dropdownFilterItemValue

    // Handle different filter types
    if (el.name === 'radius') {
      // Single-select logic for distance
      dropdown.querySelectorAll('.dropdown-filter-item').forEach(item => {
        item.classList.remove('active-item')
      })
      dropdownFilterItem.classList.add('active-item')
      el.value = selectedValue

      // Update the display text
      if (this.hasRadiusDisplayTarget) {
        this.radiusDisplayTarget.textContent = `${selectedValue} miles`
      }

      // Close dropdown and search
      dropdown.classList.add("d-none")
    } else {
      // Multi-select logic for specialty
      dropdownFilterItem.classList.toggle("active-item")
      const originValue = el.value

      if (!dropdownFilterItem.classList.contains("active-item")) {
        el.value = originValue
          .split(", ")
          .filter((value) => value !== selectedValue)
          .join(", ")
      } else if (!originValue.split(",").some((value) => value.trim() === selectedValue)) {
        el.value = originValue ? `${originValue}, ${selectedValue}` : selectedValue
      }

      this.toggleSelectAllButton(dropdown)
      this.updateSpecialtyButtonState(el)
    }

    setTimeout(() => {
      this.performSearch()
    }, 100)
  }

  selectOrDeselectAll(event) {
    const dropdown = event.target.closest(".custom-dropdown")
    const hiddenInput = dropdown.querySelector('input.filter-input[type="hidden"]')
    const items = dropdown.querySelectorAll(".dropdown-filter-item")
    const activeItems = dropdown.querySelectorAll(".dropdown-filter-item.active-item")

    if (activeItems.length > 0) {
      hiddenInput.value = ""
      items.forEach((item) => item.classList.remove("active-item"))
    } else {
      const allIds = Array.from(items).map(item => item.dataset.dropdownFilterItemValue)
      hiddenInput.value = allIds.join(", ")
      items.forEach((item) => item.classList.add("active-item"))
    }

    this.toggleSelectAllButton(dropdown)
    this.updateSpecialtyButtonState(hiddenInput)

    setTimeout(() => {
      this.performSearch()
    }, 100)
  }

  updateSpecialtyButtonState(hiddenInput) {
    if (!this.hasSpecialtyButtonTarget || hiddenInput.name !== 'specialty') return

    const active = hiddenInput.value.trim().length > 0
    this.specialtyButtonTarget.style.setProperty(
      'border-color',
      active ? '#4168FF' : 'rgba(0, 0, 0, 0.1)',
      'important'
    )
  }

  toggleSelectAllButton(dropdown) {
    const selectAllButton = dropdown.querySelector('.select-all')
    if (!selectAllButton) return

    const activeItems = dropdown.querySelectorAll(".dropdown-filter-item.active-item")

    if (activeItems.length > 0) {
      selectAllButton.textContent = "Deselect All"
    } else {
      selectAllButton.textContent = "Select All"
    }
  }

  closeAllDropdowns() {
    this.element.querySelectorAll(".custom-dropdown").forEach((dropdown) => {
      dropdown.classList.add("d-none")
    })
  }

  search(event) {
    event.preventDefault()

    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    const delay = event.target.type === "text" ? 500 : 0

    this.timeout = setTimeout(() => {
      this.performSearch()
    }, delay)
  }

  showOrganizationModal(event) {
    const button = event.currentTarget
    const locationId = button.dataset.locationId

    if (!locationId) {
      console.error('Location ID not found')
      return
    }

    fetch(`/referral_partners/organization_modal?location_id=${locationId}`, {
      method: 'GET',
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.text())
    .then(html => {
      const modalContent = document.getElementById('network-organization-modal-content')
      modalContent.innerHTML = html

      // Add event listener to the send referral button
      const sendReferralButton = modalContent.querySelector('[data-action*="sendReferral"]')
      if (sendReferralButton) {
        sendReferralButton.addEventListener('click', (e) => {
          this.sendReferral(e)
        })
      }
    })
    .catch(error => {
      console.error('Error loading modal content:', error)
    })
  }

  sendReferral(event) {
    event.preventDefault()

    const organizationId = event.currentTarget.dataset.organizationId

    if (!organizationId) {
      console.error('Organization ID not found')
      return
    }

    window.location.href = `/referrals/new?organization_id=${organizationId}`
  }

  performSearch() {
    if (!this.hasFormTarget) return
    const form = this.formTarget

    const formData = new FormData(form)
    const params = new URLSearchParams(formData)

    fetch(`${form.action}?${params}`, {
      method: 'GET',
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.text())
    .then(html => {
      if (this.hasPanelBodyTarget) {
        // Replace the network list and suggestions together; the freshly
        // rendered suggestions markup carries its own infinite-scroll
        // controller with the current search params baked into its URL.
        this.panelBodyTarget.innerHTML = html

        // Reinitialize tooltips for new content
        this.initializeTooltips()
      }
    })
    .catch(error => {
      console.error('Network search error:', error)
    })
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler)
    }
    document.removeEventListener("turbo:before-cache", this.closeOrganizationModalForCache)
  }

  closeOrganizationModalForCache() {
    const modalEl = document.getElementById("networkOrganizationModal")
    if (!modalEl || !modalEl.classList.contains("show")) return
    // Synchronous teardown: the snapshot is taken as soon as this returns,
    // before Modal#hide's fade would finish.
    Modal.getInstance(modalEl)?.dispose()
    modalEl.classList.remove("show")
    modalEl.style.display = ""
    modalEl.setAttribute("aria-hidden", "true")
    document.querySelectorAll(".modal-backdrop").forEach(el => el.remove())
    document.body.classList.remove("modal-open")
    document.body.style.overflow = ""
    document.body.style.paddingRight = ""
  }
}
